import type {MediaSize} from "./mediaFit";

/**
 * What a browser can tell us about a picked PICTURE before a byte is uploaded
 * (kitecorp/transferify-ui#2143).
 *
 * The instrument beside `videoMetadata.ts`, built the same way and for the same reason: the rule that
 * judges the answer lives in `mediaFit.ts` and is tested there against literals, while this module only
 * decodes enough of the file to learn its size - a browser API, and therefore untestable as data.
 *
 * ## Why an `<img>` and not the header reader we already have
 *
 * `core/images/imageHeader.ts` parses PNG, JPEG and WebP headers without decoding, and the compression
 * pipeline uses it. It is the WRONG instrument here, and `offscreenImageCodec.ts` says why in its own
 * words: "a header's axes are pre-orientation, so a phone photo tagged Exif 6 or 8 decodes with its
 * axes swapped". For the compressor that costs memory and never correctness. Here it IS the answer: a
 * landscape photograph shot on a phone carries portrait axes in its header, and refusing it would turn
 * "your picture is too tall" into a lie about a file that is not. `naturalWidth`/`naturalHeight` are
 * the size the browser will actually PAINT - `image-orientation: from-image` is the CSS default - which
 * is the same size the recipient's crop is taken from.
 *
 * Nothing here runs at module scope, so a pre-rendered page never touches `document` (ui#74).
 */

/**
 * How long to wait for the element to say anything at all.
 *
 * The same eight seconds `videoMetadata.ts` allows, and for the same reason: this is a ceiling on
 * SILENCE rather than a budget for work - a browser that is going to answer about a file already on the
 * reader's own disk has answered long before it.
 */
const DECODE_TIMEOUT_MS = 8_000;

/**
 * Reads a picked picture's intrinsic size, or `null` when this browser could not measure it.
 *
 * `null` is an ANSWER rather than a failure, and the shape rule fails open on it
 * (`isTooNarrowForWallpaper`): a browser that cannot decode a file has said nothing about the file, so
 * it is uploaded and the api judges it.
 *
 * The object URL is revoked on every path, including the error and timeout ones: a leaked blob URL pins
 * the whole file in memory for the life of the document, and this is a picker an advertiser retries.
 */
export function readImageSize(
	file: File, timeoutMs: number = DECODE_TIMEOUT_MS,
): Promise<MediaSize | null> {
	return new Promise<MediaSize | null>(resolve => {
		const element = document.createElement("img");
		const source = URL.createObjectURL(file);
		let settled = false;
		const settle = (size: MediaSize | null) => {
			if (settled) {
				return;
			}
			settled = true;
			window.clearTimeout(giveUp);
			URL.revokeObjectURL(source);
			element.removeAttribute("src");
			resolve(size);
		};
		/*
		 * THE PROMISE MUST SETTLE, and the two events are not enough to guarantee it - the identical
		 * hazard `videoMetadata.ts` records at length. jsdom fires neither, and the caller awaits this
		 * inside `flight.run("pick-files")`, whose key is released only when the tracked promise settles.
		 * A promise that never did would hold that key for the life of the page, hand itself to every
		 * later pick, and leave the advertiser with no spinner, no message and no retry that can work.
		 */
		const giveUp = window.setTimeout(() => settle(null), timeoutMs);
		element.onload = () => settle(
			// Zero on both axes is what a browser reports for a file it accepted but could not decode.
			// Passed through as-is: the shape rule reads it as unmeasured and admits the file.
			{width: element.naturalWidth, height: element.naturalHeight});
		// The browser could not decode these bytes at all. Not a verdict about the picture - a build
		// without a WebP decoder says exactly this about a file the api stores happily.
		element.onerror = () => settle(null);
		element.src = source;
	});
}
